import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import Stats from "../Product/stats";
import "./total_invoice.css"; // Reuse the same modern styles
import {
  Plus,
  Search,
  RefreshCcw,
  Download,
  Printer,
  Trash2,
  Pencil,
} from "lucide-react";

export default function TotalReceipts() {
  const { companyId } = useCompany();
  const navigate = useNavigate();

  const [receipts, setReceipts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [stats, setStats] = useState([
    { amount: "₦0.00", description: "Total Value of Receipts Issued" },
    { amount: 0, description: "Total Number of Receipts" },
  ]);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  // Fetch all Receipts for this company (UNCHANGED logic)
  const fetchReceipts = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase
      .from("receipts")
      .select("*")
      .eq("company_id", companyId);

    if (error) {
      console.error("Error fetching receipts:", error);
    } else {
      setReceipts(data || []);
      setFiltered(data || []);
      computeStats(data || []);
      setCurrentPage(1);
    }
  }, [companyId]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // Compute stats (kept semantics you used)
  function computeStats(list) {
    const totalAmount = list.reduce((sum, rec) => sum + (rec.total || 0), 0);
    const count = list.length;
    setStats([
      {
        amount: `₦${totalAmount.toLocaleString()}`,
        description: "Total Value of Receipts Issued",
      },
      { amount: count, description: "Total Number of Receipts" },
    ]);
  }

  // Search filter
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    const next = receipts.filter((rec) =>
      (rec.customerName || rec.customer || "").toLowerCase().includes(q)
    );
    setFiltered(next);
    setCurrentPage(1);
  }, [searchQuery, receipts]);

  // Delete (scoped to company)
  const handleDelete = async (id) => {
    const { error } = await supabase
      .from("receipts")
      .delete()
      .eq("id", id)
      .eq("company_id", companyId);

    if (error) {
      console.error("Error deleting receipts:", error);
    } else {
      const next = filtered.filter((rec) => rec.id !== id);
      setFiltered(next);
      const newAll = receipts.filter((rec) => rec.id !== id);
      setReceipts(newAll);
      computeStats(newAll);
    }
  };

  // Navigation helpers (UNCHANGED)
  const handleEdit = (id) => navigate(`/receipt/${id}`);
  const handleAdd = () => navigate("/receipt");

  // UI helpers
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageSlice = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage]);

  const refresh = () => fetchReceipts();
  const printList = () => window.print();

  const exportCSV = () => {
    const csvRows = [
      ["Customer", "Receipt #", "Date", "Total (NGN)"].join(","),
      ...filtered.map((r) =>
        [
          (r.customerName || r.customer || "").replaceAll(",", " "),
          r.receiptNumber || "",
          r.date || "",
          (r.total || 0).toString(),
        ].join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kostpadi-receipts.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="receipts-page">
      {/* HERO */}
      <header className="receipts-hero">
        <div>
          <p className="receipts-eyebrow">Sales & Collections</p>

          <h1>Total Receipts</h1>

          <p>
            Search, export, print and manage all receipts issued to customers
            for received payments.
          </p>
        </div>

        <div className="receipts-hero-actions">
          <button className="receipt-btn soft" onClick={refresh}>
            <RefreshCcw size={16} />
            Refresh
          </button>

          <button className="receipt-btn soft" onClick={printList}>
            <Printer size={16} />
            Print
          </button>

          <button className="receipt-btn soft" onClick={exportCSV}>
            <Download size={16} />
            Export
          </button>

          <button className="receipt-btn primary" onClick={handleAdd}>
            <Plus size={16} />
            New Receipt
          </button>
        </div>
      </header>

      {/* STATS */}
      <section className="receipt-stats-panel">
        <Stats stats={stats} />
      </section>

      {/* FILTERS */}
      <section className="receipt-panel">
        <div className="receipt-panel-head">
          <div>
            <h3>Search Receipts</h3>
            <p>Find receipts by customer name or receipt details.</p>
          </div>
        </div>

        <div className="receipt-filters">
          <div className="receipt-search">
            <Search size={16} />

            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by customer..."
            />
          </div>
        </div>
      </section>

      {/* LIST */}
      <section className="receipt-panel">
        <div className="receipt-panel-head">
          <div>
            <h3>Receipt Records</h3>
            <p>
              Showing {filtered.length} receipt
              {filtered.length !== 1 ? "s" : ""}.
            </p>
          </div>
        </div>

        {pageSlice.length ? (
          <div className="receipt-cards">
            {pageSlice.map((rec) => (
              <article className="receipt-card" key={rec.id}>
                <div className="receipt-card-top">
                  <div>
                    <h3 title={rec.customerName || rec.customer || "—"}>
                      {rec.customerName || rec.customer || "—"}
                    </h3>

                    <span className="receipt-doc-number">
                      #{rec.receiptNumber || "—"}
                    </span>
                  </div>

                  <span className="receipt-badge">Receipt</span>
                </div>

                <div className="receipt-card-body">
                  <div className="receipt-meta-grid">
                    <div className="receipt-meta-box">
                      <span>Date</span>
                      <strong>{rec.date || "—"}</strong>
                    </div>

                    <div className="receipt-meta-box total">
                      <span>Total Amount</span>
                      <strong>₦{(rec.total || 0).toLocaleString()}</strong>
                    </div>
                  </div>
                </div>

                <div className="receipt-card-actions">
                  <button
                    className="receipt-icon-btn"
                    onClick={() => handleEdit(rec.id)}
                    title="Edit receipt"
                  >
                    <Pencil size={16} />
                    Edit
                  </button>

                  <button
                    className="receipt-icon-btn danger"
                    onClick={() => handleDelete(rec.id)}
                    title="Delete receipt"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="receipt-empty">
            <div className="receipt-empty-icon">🧾</div>

            <h3>No receipts found</h3>

            <p>
              Create your first receipt to record customer payments and maintain
              a clear collection trail.
            </p>

            <button className="receipt-btn primary" onClick={handleAdd}>
              <Plus size={16} />
              Create Receipt
            </button>
          </div>
        )}

        {filtered.length > pageSize && (
          <div className="receipt-pagination">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`receipt-page-btn ${
                  currentPage === i + 1 ? "active" : ""
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
